import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  fetchUserGroupsRolesIncidents,
  toggleAllowAdminProfileEdit,
} from 'actions/profileActions';
import {
  updateCurrentIncidentById,
  updateCurrentlySelectedGroup,
} from 'actions/diceActions';
import LegacyPratusTable from 'components/DesignSystems/PratusTable/LegacyPratusTable';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { endLoading } from 'reducers/loading/loading.action';
import { changeGroupAdminNameToOrgAdmin } from '../Roles/RolesUtils/groupToOrgTransformations';

export default function AdminUser({ user }) {
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const [mappedTableData, setMappedTableData] = React.useState([]);

  const reduxGroups =
    useSelector((state) => {
      return state.app.groups;
    }) || [];

  const userGroupsRolesIncidents = useSelector((state) => {
    return state.app.userGroupsRolesIncidents;
  });

  const isFetchUserGroupsRolesIncidentsLoaded = useSelector((state) => {
    return state.app.isFetchUserGroupsRolesIncidentsLoaded;
  });
  const alluserWorkspaces = useSelector((state) => {
    return state.app.alluserWorkspaces;
  });
  useEffect(() => {
    if (
      (!userGroupsRolesIncidents || userGroupsRolesIncidents.length === 0) &&
      !!isFetchUserGroupsRolesIncidentsLoaded
    ) {
      reduxDispatch(fetchUserGroupsRolesIncidents(user.user_guid));
    }
  }, [
    isFetchUserGroupsRolesIncidentsLoaded,
    reduxDispatch,
    user.user_guid,
    userGroupsRolesIncidents,
  ]);

  useEffect(() => {
    reduxDispatch(endLoading());
    if (!!userGroupsRolesIncidents && userGroupsRolesIncidents.length > 0) {
      const tableData = userGroupsRolesIncidents.map((ugri) => {
        const workspace = alluserWorkspaces
          ? alluserWorkspaces
              .filter((e) => e.organizationId === ugri.group_guid)
              .map((e) => e.displayName)
              .join(', ')
          : '';

        const groupSubscription = user.subscriptions?.find(
          (s) => s.group_guid === ugri.group_guid
        );

        return {
          ...ugri,
          roles: ugri.group_roles.map((gr) => changeGroupAdminNameToOrgAdmin(gr.name)).join(', '),
          incidents: ugri.group_incidents.map((gi) => gi.name).join(', '),
          workspace: workspace,
          subscription_status: groupSubscription?.status,
        };
      });
      setMappedTableData(tableData);
    } else {
      setMappedTableData([]);
    }
  }, [
    userGroupsRolesIncidents,
    alluserWorkspaces,
    reduxDispatch,
    user.subscriptions,
  ]);

  const allColumns = [
    {
      dataField: 'group_name',
      text: 'Group Memberships',
      sort: true,
      headerAttrs: { title: 'Sort By:' },
      attrs: { title: 'Group Memberships' },
    },
    {
      dataField: 'subscription_status',
      text: 'Subscription',
      sort: true,
      attrs: { title: 'Subscription Status' },
    },
    {
      dataField: 'roles',
      text: 'Organization Roles',
      sort: true,
      attrs: { title: 'Organization Roles' },
    },
    {
      dataField: 'workspace',
      text: 'Exercise',
      sort: true,
      attrs: { title: 'Exercise' },
    },
    {
      dataField: 'incidents',
      text: 'Incidents',
      sort: true,
      attrs: { title: 'Incidents' },
    },
  ];

  const handleIncidentLinkClick = async (incidentId, groupId) => {
    await reduxDispatch(
      updateCurrentlySelectedGroup(
        reduxGroups.find((g) => g.group_guid === groupId)
      )
    );
    reduxDispatch(updateCurrentIncidentById(incidentId));
    navigate(`/iap/${incidentId}/settings`);
  };

  const handleGroupLinkClick = (groupId) => {
    reduxDispatch(
      updateCurrentlySelectedGroup(
        reduxGroups.find((g) => g.group_guid === groupId)
      )
    );
    navigate('/groups');
  };

  function handleAdminProfileEditToggle(group_guid) {
    reduxDispatch(toggleAllowAdminProfileEdit(group_guid));
  }

  const expandRow = {
    parentClassName: 'parent-expand-tr',
    className: 'child-expand-tr',
    renderer: (row) => {
      return (
        <>
          <div className="row">
            <div className="col-md-4 mb-3 mb-md-4">
              <h6 className="mb-2">Group</h6>
              <button
                className="button button--link anchor anchor--white mb-2"
                onClick={() => handleGroupLinkClick(row.group_guid)}
                style={{ padding: 0, lineHeight: 'inherit', margin: 0 }}
              >
                {row.group_name}
              </button>
            </div>
            <div className="col-md-4 mb-3 mb-md-4">
              <h6 className="mb-2">Organization Roles</h6>
              {!!row.group_roles && !!row.group_roles.length ? (
                <>
                  <ul className="list mb-2">
                    {row.group_roles.map((ra) => {
                      return <li key={'AdminUser-' + ra.name}>{changeGroupAdminNameToOrgAdmin(ra.name)}</li>;
                    })}
                  </ul>
                </>
              ) : null}
            </div>
            <div className="col-md-4 mb-3 mb-md-4">
              <h6 className="mb-2">Incidents</h6>
              {!!row.group_incidents && !!row.group_incidents.length ? (
                <>
                  <ul className="list mb-2">
                    {row.group_incidents.map((i) => {
                      return (
                        <li key={i.id}>
                          <button
                            className="button button--link anchor anchor--white m-0"
                            onClick={() =>
                              handleIncidentLinkClick(i.id, row.group_guid)
                            }
                            style={{
                              padding: 0,
                              lineHeight: 'inherit',
                              margin: 0,
                            }}
                          >
                            {i.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null}
            </div>
            <hr className="dashed m-0" />
            <div className="col-md-12 mt-4">
              <StylishNewCheckbox
                checked={row.allow_admin_profile_edits}
                label={`The org admin can edit my profile`}
                onClick={() => handleAdminProfileEditToggle(row.group_guid)}
              />
            </div>
          </div>
        </>
      );
    },
    showExpandColumn: true,
  };

  return (
    <>
      {(!!userGroupsRolesIncidents &&
        !!isFetchUserGroupsRolesIncidentsLoaded && (
          <>
            <LegacyPratusTable
              keyField={'group_guid'}
              columns={allColumns}
              rows={mappedTableData}
              expandRow={expandRow}
            />
          </>
        )) || (
        <p className="weight-500 m-0">Loading User Groups and Incidents...</p>
      )}
    </>
  );
}
